import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { today, yearStart } from "@/lib/format";
import { DateRange, EmptyRow, Field, Money, NativeSelect, PageHeader, Panel } from "@/components/common";
import { ExportButtons } from "@/components/ExportButtons";

export default function Ledger() {
  const { t, accName } = useLang();
  const [accounts, setAccounts] = useState([]);
  const [aid, setAid] = useState("");
  const [range, setRange] = useState({ start: yearStart(), end: today() });
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/accounts").then((r) => {
      setAccounts(r.data);
      setAid(r.data.find((a) => a.code === "1100")?.id || r.data[0]?.id || "");
    });
  }, []);
  useEffect(() => {
    if (aid) api.get("/reports/ledger", { params: { account_id: aid, ...range } }).then((r) => setData(r.data));
  }, [aid, range]);

  return (
    <div data-testid="ledger-page">
      <PageHeader title={t("ledger")} subtitle={t("ledgerSubtitle")}
        actions={<div className="flex flex-wrap items-center gap-2"><span className="text-xs text-slate-400">{t("exportAccount")}</span><ExportButtons path="/export/ledger" params={{ account_id: aid, ...range }} testid="ledger-export" /><span className="text-xs text-slate-400">{t("exportAll")}</span><ExportButtons path="/export/ledger" params={range} csv={false} testid="ledger-export-all" /></div>} />
      <Panel className="mb-6 flex flex-wrap items-end gap-4 p-5">
        <Field label={t("account")} className="min-w-[280px] flex-1">
          <NativeSelect data-testid="ledger-account-select" value={aid} onChange={(e) => setAid(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {accName(a)}</option>)}
          </NativeSelect>
        </Field>
        <DateRange testid="ledger-range" start={range.start} end={range.end} onChange={(start, end) => setRange({ start, end })} />
      </Panel>
      {data && (
        <Panel className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>{t("date")}</th><th>{t("reference")}</th><th>{t("description")}</th><th className="text-end">{t("debit")}</th><th className="text-end">{t("credit")}</th><th className="text-end">{t("balance")}</th></tr></thead>
            <tbody>
              <tr className="bg-slate-50 font-semibold"><td colSpan={5}>{t("openingBalance")}</td><td className="text-end"><Money testid="ledger-opening" value={data.opening_balance} /></td></tr>
              {data.lines.length === 0 && <EmptyRow cols={6} />}
              {data.lines.map((l, i) => (
                <tr key={i}>
                  <td className="font-mono text-slate-500">{l.date}</td>
                  <td className="font-mono">{l.reference}</td>
                  <td className="text-slate-600">{l.description}</td>
                  <td className="text-end">{l.debit ? <Money value={l.debit} cur={false} /> : ""}</td>
                  <td className="text-end">{l.credit ? <Money value={l.credit} cur={false} /> : ""}</td>
                  <td className="text-end font-semibold"><Money value={l.balance} cur={false} /></td>
                </tr>
              ))}
              <tr className="bg-[#0B1220] font-semibold text-white"><td colSpan={5}>{t("closingBalance")}</td><td className="text-end"><Money testid="ledger-closing" value={data.closing_balance} /></td></tr>
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
